import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { setTenantScope } from './useDataCache';

interface User {
  id: number;
  fullName: string;
  email: string;
  emailVerified: boolean;
  tenantId: number;
  tenantSlug?: string;
  role?: string;
  permissions?: string[];
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string, tenantSlug: string) => Promise<void>;
  logout: () => Promise<void>;
  signup: (data: SignupData) => Promise<void>;
}

interface SignupData {
  empresa: string;
  nombreCompleto: string;
  email: string;
  usuario: string;
  contraseña: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentTenantSlug, setCurrentTenantSlug] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Recover tenant slug from localStorage on mount
    const savedTenantSlug = localStorage.getItem('currentTenantSlug');
    if (savedTenantSlug) {
      setCurrentTenantSlug(savedTenantSlug);
    }
    // Check if user is already authenticated
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.user) {
          setUser(result.user);
          // CRITICAL: Set tenant scope for data cache after successful refresh
          setTenantScope(result.user.tenantId.toString());
          // Get tenant slug for this user if not already available
          if (!currentTenantSlug) {
            fetchTenantSlug();
          }
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch tenant slug from system info
  const fetchTenantSlug = async () => {
    try {
      const response = await fetch('/api/system/info', {
        credentials: 'include',
      });
      if (response.ok) {
        const result = await response.json();
        setCurrentTenantSlug(result.tenantId); // API returns slug as tenantId
      }
    } catch (error) {
      console.error('Failed to fetch tenant slug:', error);
    }
  };

  const login = async (email: string, password: string, tenantSlug: string) => {
    setIsLoading(true);
    try {
      const response = await fetch(`/api/auth/login/${tenantSlug}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });

      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error('El servidor no está disponible. Por favor intente de nuevo en unos momentos.');
      }

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Login failed');
      }

      const result = await response.json();
      setUser(result.user);
      // CRITICAL: Set tenant scope after successful login to prevent cross-tenant data
      if (result.user && result.user.tenantId) {
        setTenantScope(result.user.tenantId.toString());
      }
      // Store the tenant slug from the login URL for logout purposes and page reloads
      setCurrentTenantSlug(tenantSlug);
      localStorage.setItem('currentTenantSlug', tenantSlug);
      
      // Redirect based on user role
      if (result.user.role === 'Alumno') {
        navigate('/alumno');
      } else {
        navigate('/app');
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      // Use stored tenant slug or fallback to fetching from system info
      let tenantSlug = currentTenantSlug;
      
      if (!tenantSlug) {
        // Try to get tenant slug from system info before logout
        try {
          const infoResponse = await fetch('/api/system/info', {
            credentials: 'include',
          });
          if (infoResponse.ok) {
            const infoResult = await infoResponse.json();
            tenantSlug = infoResult.tenantId; // API returns slug as tenantId
          }
        } catch (infoError) {
          console.error('Failed to fetch tenant slug before logout:', infoError);
        }
      }
      
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
      });
      
      // CRITICAL: Clear tenant scope and cache on logout to prevent cross-tenant data exposure
      setTenantScope(null);
      setUser(null);
      setCurrentTenantSlug(null);
      localStorage.removeItem('currentTenantSlug');
      
      // Redirect to the current tenant's login page instead of default
      const redirectTenant = tenantSlug || 'testcampus';
      navigate(`/${redirectTenant}/login`);
    } catch (error) {
      console.error('Logout error:', error);
      // CRITICAL: Clear tenant scope and cache even on error
      setTenantScope(null);
      setUser(null);
      setCurrentTenantSlug(null);
      localStorage.removeItem('currentTenantSlug');
      
      // Fallback to testcampus on error
      navigate('/testcampus/login');
    }
  };

  const signup = async (data: SignupData) => {
    setIsLoading(true);
    try {
      const response = await fetch('/api/tenants/signup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Signup failed');
      }

      const result = await response.json();
      setUser(result.user);
      // CRITICAL: Set tenant scope after successful signup to enable data cache
      if (result.user && result.user.tenantId) {
        setTenantScope(result.user.tenantId.toString());
      }
      navigate('/app');
    } catch (error) {
      console.error('Signup error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    user,
    isLoading,
    login,
    logout,
    signup,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}